import { useState } from "react"
import { FullscreenOutlined } from "@ant-design/icons"
import { Image, Tooltip } from "antd"
import type { ImageProps } from "antd"
import classNames from "classnames"
import {
  defaultPreviewFrameSize,
  getPreviewFrameSizeClassName,
  previewFrameClassName,
  type PreviewFrameSize,
} from "~/components/ResourcePreview/previewFrame"
import styles from "./index.css"

type ImageThumbnailProps = Omit<
  ImageProps,
  "height" | "rootClassName" | "width"
> & {
  size?: PreviewFrameSize
}

const renderPreviewMask = () => {
  return (
    <span className={styles.previewMask}>
      <FullscreenOutlined />
    </span>
  )
}

const getPreviewConfig = (
  preview: ImageThumbnailProps["preview"],
  open: boolean,
  onOpenChange: (open: boolean) => void,
) => {
  if (preview === false) return false

  if (preview && typeof preview === "object") {
    const { open: configuredOpen, visible, ...config } = preview

    return {
      ...config,
      open: configuredOpen ?? visible ?? open,
      onOpenChange,
    }
  }

  return {
    open,
    onOpenChange,
  }
}

export const ImageThumbnail = ({
  className,
  preview,
  size = defaultPreviewFrameSize,
  ...props
}: ImageThumbnailProps) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const previewEnabled = preview !== false
  const handlePreviewOpenChange = (open: boolean) => {
    setPreviewOpen(open)

    if (preview && typeof preview === "object") {
      preview.onOpenChange?.(open)
      preview.onVisibleChange?.(open, !open)
    }
  }
  const previewConfig = getPreviewConfig(
    preview,
    previewOpen,
    handlePreviewOpenChange,
  )

  return (
    <div
      className={classNames(
        previewFrameClassName,
        getPreviewFrameSizeClassName(size),
        styles.image,
        className,
      )}
    >
      <Image
        {...props}
        preview={false}
        classNames={{
          root: styles.imageContent,
          image: styles.imageInner,
        }}
      />
      {previewEnabled ? (
        <>
          <span
            aria-hidden
            className={styles.previewShade}
          />
          <Tooltip title="查看原图">
            <button
              aria-label="查看原图"
              className={styles.previewButton}
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                handlePreviewOpenChange(true)
              }}
            >
              {renderPreviewMask()}
            </button>
          </Tooltip>
          <Image
            alt={props.alt}
            classNames={{ root: styles.hiddenPreview }}
            preview={previewConfig}
            src={props.src}
          />
        </>
      ) : null}
    </div>
  )
}
